/**
 * member-search.tsx — Search input and results table for member record lookup.
 *
 * Supports search by name, DOB, or patient ID (internal ULID).
 * PHI constraint:
 * - Search criteria are submitted via POST body, NOT query string.
 *   This prevents PHI from appearing in ALB/CloudFront access logs (Issue #8 fix).
 * - Response contains only memberId, firstName, lastName, and program summaries.
 *   No MBI or DOB in the response (Issue #3 fix).
 *
 * Roles: staff:admin or staff:wa (enforced by RequireMergeRole guard in app.tsx).
 */

import { useState, useCallback } from 'react';
import type { JSX } from 'react';

export interface MemberSearchResult {
  memberId: string; // ULID — internal identifier only
  firstName: string;
  lastName: string;
  programEnrollments: Array<{
    programId: string;
    status: 'active' | 'completed' | 'withdrawn' | 'deprecated';
  }>;
}

export interface MemberSearchProps {
  onSelect: (memberId: string) => void;
  label: string;
  selectedMemberId?: string | undefined;
}

export function MemberSearch({
  onSelect,
  label,
  selectedMemberId,
}: MemberSearchProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = useCallback(async (): Promise<void> => {
    if (query.trim().length === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      // PHI constraint: search criteria in POST body, NOT query string.
      // This prevents name/DOB from appearing in ALB/CloudFront access logs.
      const response = await fetch('/v1/members/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`Search failed with status ${String(response.status)}`);
      }

      const data = (await response.json()) as { items: MemberSearchResult[] };
      setResults(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query]);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e): void => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e): void => {
            if (e.key === 'Enter') {
              void handleSearch();
            }
          }}
          placeholder="Search by name, date of birth (YYYY-MM-DD), or member ID…"
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          aria-label="Search query"
        />
        <button
          type="button"
          onClick={(): void => {
            void handleSearch();
          }}
          disabled={isLoading}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error !== null && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {results.length > 0 && (
        <div className="overflow-hidden rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200" role="table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Member ID
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Programs
                </th>
                <th className="px-4 py-2">
                  <span className="sr-only">Select</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {results.map((member) => (
                <tr
                  key={member.memberId}
                  className={selectedMemberId === member.memberId ? 'bg-blue-50' : ''}
                >
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {member.firstName} {member.lastName}
                  </td>
                  <td className="px-4 py-2 font-mono text-sm text-gray-600">{member.memberId}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {member.programEnrollments.length}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={(): void => {
                        onSelect(member.memberId);
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      aria-label={`Select member ${member.memberId}`}
                    >
                      {selectedMemberId === member.memberId ? 'Selected' : 'Select'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {results.length === 0 && !isLoading && query.length > 0 && error === null && (
        <p className="text-sm text-gray-500">No members found for this query.</p>
      )}
    </div>
  );
}
